import { TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { useEffect, useState } from 'react'
import { AgingRange, getServicingReport, saveServicingReport } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

export default function ServicingReport() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(true)
  const [agingMatrix, setAgingMatrix] = useState<AgingRange[]>([])
  const [valueChanged, setValueChanged] = useState(false)

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      const matrix = res?.agingMatrix || res?.data?.agingMatrix || []
      setAgingMatrix(Array.isArray(matrix) ? matrix : [])
      setLoading(false)
      return
    }
    setLoading(true)
    setLoanNumber()
    getServicingReport()
      .then((res: any) => {
        const matrix = res.agingMatrix || [
          { start: 1, end: 30 },
          { start: 31, end: 90 },
          { start: 91, end: 180 },
          { start: 181, end: null },
        ]
        setAgingMatrix(matrix)
      })
      .finally(() => setLoading(false))
  }, [isShared, shared?.pageKey, shared?.pageData])

  const handleAddRange = () => {
    if (isShared) return
    const newRange: AgingRange = { start: 0, end: 0 }
    setAgingMatrix([...agingMatrix, newRange])
    setValueChanged(true)
  }

  const handleRemoveRange = (index: number) => {
    if (isShared) return
    const newMatrix = agingMatrix.filter((_, i) => i !== index)
    setAgingMatrix(newMatrix)
    setValueChanged(true)
  }

  const handleRangeChange = (index: number, field: 'start' | 'end', value: string) => {
    if (isShared) return
    const newMatrix = [...agingMatrix]
    const numValue = value === '' ? null : parseInt(value, 10)

    if (field === 'end' && value === '') {
      newMatrix[index][field] = null
    } else if (numValue !== null && !isNaN(numValue)) {
      newMatrix[index][field] = numValue as any
    } else if (value === '') {
      newMatrix[index][field] = 0 as any
    }

    setAgingMatrix(newMatrix)
    setValueChanged(true)
  }

  const handleSave = async () => {
    if (isShared) return
    setLoading(true)
    try {
      await saveServicingReport({ agingMatrix })
      setValueChanged(false)
    } catch (error) {
      console.error('Error saving servicing report:', error)
    } finally {
      setLoading(false)
    }
  }

  const createInputConfig = (index: number, field: 'start' | 'end', value: number | null): InputType => {
    return {
      inputType: 'text',
      type: 'thousandSepNoDecimal',
      title: field === 'start' ? 'Start' : 'End',
      value: value === null ? '' : value,
      placeholder: field === 'end' ? 'No limit' : '',
      disabled: isShared,
      readOnly: isShared,
    }
  }

  return (
    <div>
      <LayoutLoading show={loading} />
      {!isShared && <SaveChanges show={valueChanged} label="Save Changes" onSave={handleSave} />}

      <div className="mb-6">
        <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
          <span>Servicing Report</span>
        </h2>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Aging Matrix</h3>
          {!isShared && (
            <Button onClick={handleAddRange} disabled={loading}>
              + Add Range
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {agingMatrix.map((range, index) => {
            const startInput = createInputConfig(index, 'start', range.start)
            const endInput = createInputConfig(index, 'end', range.end)

            return (
              <div key={index} className={`flex items-center gap-4 p-4 bg-white ${index % 2 ? 'bg-slate-50' : ''}`}>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <RenderInput
                      input={startInput}
                      Key={`range-${index}-start`}
                      onChange={(key: string, value: any) => handleRangeChange(index, 'start', value)}
                    />
                  </div>
                  <div>
                    <div className="relative">
                      <RenderInput
                        input={endInput}
                        Key={`range-${index}-end`}
                        onChange={(key: string, value: any) => handleRangeChange(index, 'end', value)}
                      />
                    </div>
                  </div>
                </div>
                {!isShared && (
                  <div className="flex items-center">
                    <Button
                      color="red"
                      onClick={() => handleRemoveRange(index)}
                      disabled={loading || agingMatrix.length <= 1}
                      className="!p-2"
                      title="Remove range"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {agingMatrix.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {isShared ? 'No aging ranges configured.' : 'No aging ranges configured. Click "Add Range" to create one.'}
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Preview</h4>
          <div className="text-sm text-blue-800">
            {agingMatrix.map((range, index) => (
              <div key={index}>
                {range.start} - {range.end === null ? '∞' : range.end} days
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
